import "../Summary/components/style.css";
import { useTranslation } from "react-i18next";
import CardWorkflowInfo from "../Summary/components/CardWorkflowInfo";
import { red } from "@mui/material/colors";
import UserWorkflowInfo from "../Summary/components/UserWorkflowInfo";
import Notification from "../Summary/components/Notification";
import moment from "moment";
import "moment/locale/vi";
import {
  Button,
  ButtonGroup,
  Box,
  Menu,
  MenuItem,
  TextField,
  InputAdornment
} from "@mui/material";
import { LineChart, lineElementClasses } from "@mui/x-charts/LineChart";
import CustomTooltipContent from "../Summary/components/CustomTooltip";
import ProgressLine from "../Summary/components/ProgressLine";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "../../assets/images/analytics/chevron_up_down.svg";
import DonutChart from "../Summary/components/DonutChart";
import SearchIcon from "@mui/icons-material/Search";
import DataTable from "../Summary/components/Table";

import { useState, useEffect, useRef } from "react";
import TaskService from "../../services/api/task.service";
import WorkflowService from "../../services/api/workflow.service";
import NotificationService from "../../services/api/notification.service";
import { useApi } from "../../hooks/useApi.hook";
import {
  ITaskStatistics,
  ITaskStatisticsResponse,
  IUserTaskCount,
  ITaskByMonthResponse,
  IWorkflowStatistics,
  IBoardStatistics
} from "../../models/Task";
import { IGroupedBoardsResponse } from "../../models/workflow";
import {
  IGroupNotification,
  INotificationCard
} from "../../models/notification";
import NestedMenuItem from "../Summary/components/NestedMenuItem";
import variableStyles from "../../theme/variable-styles";

export default function SummaryAdmin() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [workflows, setWorkflows] = useState<ITaskStatistics[]>([]);
  const [usersWorkflowInfo, setUsersWorkflowInfo] = useState<IUserTaskCount[]>(
    []
  );
  const [sortedUsers, setSortedUsers] = useState<IUserTaskCount[]>([]);
  const [sortBy, setSortBy] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [monthlyTasksData, setMonthlyTasksData] =
    useState<ITaskByMonthResponse | null>(null);
  const [completedTasksData, setCompletedTasksData] =
    useState<ITaskByMonthResponse | null>(null);
  const [loadingCompletedTasks, setLoadingCompletedTasks] =
    useState<boolean>(false);
  const [chartViewMode, setChartViewMode] = useState<
    "completion" | "taskCount"
  >("taskCount");
  const [groupedBoards, setGroupedBoards] =
    useState<IGroupedBoardsResponse | null>(null);
  const [boardStatistics, setBoardStatistics] =
    useState<IBoardStatistics | null>(null);
  const [selectedBoardName, setSelectedBoardName] = useState<string>("");
  const [loadingWorkflowStats, setLoadingWorkflowStats] =
    useState<boolean>(false);
  const [notifications, setNotifications] = useState<IGroupNotification[]>([]);
  const [notificationPage, setNotificationPage] = useState<number>(1);
  const [hasMoreNotifications, setHasMoreNotifications] =
    useState<boolean>(true);
  const notificationScrollRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const sortOpen = Boolean(sortAnchorEl);

  // Use useApi hook for notifications (giống DashboardNavbar)
  const getNotifications = useApi<INotificationCard>({
    isFetch: false, // Fetch manual qua fetchNotifications()
    isPaging: true
  });

  // Helper function to normalize Vietnamese text (remove diacritics)
  const normalizeVietnamese = (str: string): string => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D")
      .toLowerCase();
  };

  // Filtered users based on search value with full-text search
  const filteredUsers = sortedUsers.filter(user => {
    if (!searchValue) return true;

    const normalizedSearch = normalizeVietnamese(searchValue);
    const normalizedName = normalizeVietnamese(user.userFullName);

    // Search in full name or search each word in the name
    return normalizedName.includes(normalizedSearch);
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option: string) => {
    console.log("Selected:", option);
    // Xử lý logic khi chọn option
    handleClose();
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortMenuItemClick = (sortOption: string) => {
    console.log("Sort by:", sortOption);
    setSortBy(sortOption);

    let sorted = [...usersWorkflowInfo];

    switch (sortOption) {
      case "inprogress-desc":
        sorted.sort((a, b) => b.inProgressTasks - a.inProgressTasks);
        break;
      case "inprogress-asc":
        sorted.sort((a, b) => a.inProgressTasks - b.inProgressTasks);
        break;
      case "failed-desc":
        sorted.sort((a, b) => b.failedTasks - a.failedTasks);
        break;
      case "failed-asc":
        sorted.sort((a, b) => a.failedTasks - b.failedTasks);
        break;
      case "completed-desc":
        sorted.sort((a, b) => b.completedTasks - a.completedTasks);
        break;
      case "completed-asc":
        sorted.sort((a, b) => a.completedTasks - b.completedTasks);
        break;
      case "cancelled-desc":
        sorted.sort((a, b) => b.cancelledTasks - a.cancelledTasks);
        break;
      case "cancelled-asc":
        sorted.sort((a, b) => a.cancelledTasks - b.cancelledTasks);
        break;
      case "total-desc":
        sorted.sort((a, b) => b.totalTasks - a.totalTasks);
        break;
      case "total-asc":
        sorted.sort((a, b) => a.totalTasks - b.totalTasks);
        break;
      default:
        sorted = [...usersWorkflowInfo];
    }

    setSortedUsers(sorted);
    handleSortClose();
  };

  const locale = localStorage.getItem("language");
  moment.locale(locale === "VI" ? "vi" : "en");

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Get sort label for display
  const getSortLabel = () => {
    const sortLabels: { [key: string]: string } = {
      "total-desc": t("Summary.Sort.TotalDesc"),
      "total-asc": t("Summary.Sort.TotalAsc"),
      "inprogress-desc": t("Summary.Sort.InProgressDesc"),
      "inprogress-asc": t("Summary.Sort.InProgressAsc"),
      "failed-desc": t("Summary.Sort.FailedDesc"),
      "failed-asc": t("Summary.Sort.FailedAsc"),
      "completed-desc": t("Summary.Sort.CompletedDesc"),
      "completed-asc": t("Summary.Sort.CompletedAsc"),
      "cancelled-desc": t("Summary.Sort.CancelledDesc"),
      "cancelled-asc": t("Summary.Sort.CancelledAsc")
    };
    return sortLabels[sortBy] || t("Summary.Sort.Default");
  };

  // Format ngày với chữ cái đầu viết hoa
  const formattedDate = capitalizeFirstLetter(
    moment().format("dddd, DD/MM/YYYY")
  );

  // Fetch completed tasks data từ API - SỬ DỤNG API FOR ADMIN
  const fetchCompletedTasksData = async () => {
    if (completedTasksData) {
      // Nếu đã có data rồi thì không fetch lại
      return;
    }

    try {
      setLoadingCompletedTasks(true);
      const response = await TaskService.getCompletedTaskByMonthForAdmin();

      if (response.success && response.data) {
        console.log("completedTasksData:::", response.data);
        setCompletedTasksData(response.data);
      } else {
        setCompletedTasksData(null);
      }
    } catch (error) {
      console.error("Error fetching completed tasks data:", error);
      setCompletedTasksData(null);
    } finally {
      setLoadingCompletedTasks(false);
    }
  };

  // Handle chart view mode change
  const handleChartViewModeChange = (mode: "completion" | "taskCount") => {
    setChartViewMode(mode);
    if (mode === "completion") {
      fetchCompletedTasksData();
    }
  };

  // Fetch board statistics by board ID - SỬ DỤNG API FOR ADMIN
  const fetchBoardStatistics = async (boardId: string, boardName: string) => {
    try {
      setLoadingWorkflowStats(true);
      const response =
        await TaskService.getTaskStatisticsByBoardForAdmin(boardId);

      if (response.success && response.data) {
        console.log("boardStatistics:::", response.data);
        setBoardStatistics(response.data);
        setSelectedBoardName(boardName);
      } else {
        setBoardStatistics(null);
        setSelectedBoardName("");
      }
    } catch (error) {
      console.error("Error fetching board statistics:", error);
      setBoardStatistics(null);
      setSelectedBoardName("");
    } finally {
      setLoadingWorkflowStats(false);
    }
  };

  // Fetch notifications function (giống DashboardNavbar)
  function fetchNotifications(reset = false) {
    getNotifications.request(
      NotificationService.getPage({
        CurrentPage: reset ? 1 : (notificationPage ?? 1),
        PageSize: 20
      })
    );

    if (reset) {
      setNotifications([]); // reset trước khi API trả về
      setNotificationPage(1);
    }
  }

  // Load more notifications khi scroll
  const loadMoreNotifications = () => {
    if (getNotifications.loading || !hasMoreNotifications) return;

    const nextPage = notificationPage + 1;
    setNotificationPage(nextPage);
  };

  // Listen getNotifications.data (giống DashboardNavbar)
  useEffect(() => {
    console.log("list:::", getNotifications.data);

    if (getNotifications.data) {
      const list = getNotifications.data.groupNotification ?? [];
      // Nếu notificationPage === 1 (reset) thì ghi đè, còn >1 thì nối thêm
      if (notificationPage === 1) {
        setNotifications(list);
      } else {
        setNotifications(prev => [...prev, ...list]);
      }

      // Check nếu đã hết trang
      if (
        getNotifications.pagging &&
        getNotifications.pagging.currentPage >=
          getNotifications.pagging.totalPages
      ) {
        setHasMoreNotifications(false);
      }
    }
  }, [getNotifications.data]);

  // Initial fetch notifications khi mount
  useEffect(() => {
    fetchNotifications(true); // fetch lần đầu
  }, []);

  // Fetch notifications khi notificationPage thay đổi
  useEffect(() => {
    if (notificationPage > 1) {
      fetchNotifications(false); // append khi đổi page
    }
  }, [notificationPage]);

  // Handle scroll event
  const handleNotificationScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Khi scroll gần đến cuối (còn 50px)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreNotifications();
    }
  };

  // Fetch workflows statistics và users workflow info từ API - SỬ DỤNG API FOR ADMIN
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [
          statisticsResponse,
          usersResponse,
          monthlyTasksResponse,
          boardsResponse
        ] = await Promise.all([
          TaskService.getStatisticsForAdmin(),
          TaskService.getTaskCountByUserForAdmin(),
          TaskService.getTaskByMonthForAdmin(),
          WorkflowService.getAllBoardsForAdmin()
        ]);

        if (statisticsResponse.success && statisticsResponse.data) {
          const data: ITaskStatisticsResponse = statisticsResponse.data;

          const transformedData: ITaskStatistics[] = [
            {
              title: t("Summary.Cards.TotalTasks"),
              total_yesterday: data.comparison.lastMonthTotalTasks,
              total_today: data.comparison.thisMonthTotalTasks,
              changePercent: data.comparison.totalTasksChangePercent,
              change: data.comparison.totalTasksChange
            },
            {
              title: t("Summary.Cards.CompletedTasks"),
              total_yesterday: data.comparison.lastMonthCompletedTasks,
              total_today: data.comparison.thisMonthCompletedTasks,
              changePercent: data.comparison.completedTasksChangePercent,
              change: data.comparison.completedTasksChange
            },
            {
              title: t("Summary.Cards.FailedTasks"),
              total_yesterday: data.comparison.lastMonthFailedTasks,
              total_today: data.comparison.thisMonthFailedTasks,
              changePercent: data.comparison.failedTasksChangePercent,
              change: data.comparison.failedTasksChange
            },
            {
              title: t("Summary.Cards.InProgressTasks"),
              total_yesterday: data.comparison.lastMonthInProgressTasks,
              total_today: data.comparison.thisMonthInProgressTasks,
              changePercent: data.comparison.inProgressTasksChangePercent,
              change: data.comparison.inProgressTasksChange
            },
            {
              title: t("Summary.Cards.CancelledTasks"),
              total_yesterday: data.comparison.lastMonthCancelledTasks,
              total_today: data.comparison.thisMonthCancelledTasks,
              changePercent: data.comparison.cancelledTasksChangePercent,
              change: data.comparison.cancelledTasksChange
            }
          ];

          setWorkflows(transformedData);
        } else {
          setWorkflows([]);
        }

        // Xử lý users data
        if (usersResponse.success && usersResponse.data) {
          setUsersWorkflowInfo(usersResponse.data);
          setSortedUsers(usersResponse.data); // Initialize sorted users
        } else {
          setUsersWorkflowInfo([]);
          setSortedUsers([]);
        }

        // Xử lý monthly tasks data
        if (monthlyTasksResponse.success && monthlyTasksResponse.data) {
          setMonthlyTasksData(monthlyTasksResponse.data);
        } else {
          setMonthlyTasksData(null);
        }

        // Xử lý boards with tasks data - API đã trả về đúng format IGroupedBoardsResponse
        if (boardsResponse.success && boardsResponse.data) {
          setGroupedBoards(boardsResponse.data);
          console.log("groupedBoards:::", boardsResponse.data);
        } else {
          setGroupedBoards(null);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setWorkflows([]);
        setUsersWorkflowInfo([]);
        setSortedUsers([]);
        setGroupedBoards(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [t]); // dùng t() trong transformedData nên thêm t vào dependency

  // Compute chart data based on view mode and monthly tasks data
  const getChartData = () => {
    // Nếu là completion mode, sử dụng completedTasksData
    if (chartViewMode === "completion") {
      if (!completedTasksData || !completedTasksData.monthlyTasks) {
        return {
          data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          labels: [
            t("Common.Month1"),
            t("Common.Month2"),
            t("Common.Month3"),
            t("Common.Month4"),
            t("Common.Month5"),
            t("Common.Month6"),
            t("Common.Month7"),
            t("Common.Month8"),
            t("Common.Month9"),
            t("Common.Month10"),
            t("Common.Month11"),
            t("Common.Month12")
          ]
        };
      }

      const monthlyTasks = completedTasksData.monthlyTasks;
      const labels = monthlyTasks.map(task => task.monthName);
      // Completion rate: (completedCount / taskCount) * 100
      const data = monthlyTasks.map(task => {
        if (task.taskCount === 0) return 0;
        return Math.round((task.completedCount / task.taskCount) * 100);
      });

      return { data, labels };
    }

    // Nếu là taskCount mode, sử dụng monthlyTasksData
    if (!monthlyTasksData || !monthlyTasksData.monthlyTasks) {
      return {
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        labels: [
          t("Common.Month1"),
          t("Common.Month2"),
          t("Common.Month3"),
          t("Common.Month4"),
          t("Common.Month5"),
          t("Common.Month6"),
          t("Common.Month7"),
          t("Common.Month8"),
          t("Common.Month9"),
          t("Common.Month10"),
          t("Common.Month11"),
          t("Common.Month12")
        ]
      };
    }

    const monthlyTasks = monthlyTasksData.monthlyTasks;
    const labels = monthlyTasks.map(task => task.monthName);
    const data = monthlyTasks.map(task => task.taskCount);

    return { data, labels };
  };

  const { data: chartData, labels: chartLabels } = getChartData();

  // Transform board statistics to DonutChart format
  const getDonutChartData = () => {
    if (!boardStatistics) {
      // Khi chưa chọn board, hiển thị tất cả là 0
      return [
        { id: 0, value: 0, label: t("Management.Done"), color: "#20DF75" },
        { id: 1, value: 0, label: t("Management.Fail"), color: "#FF4655" },
        {
          id: 2,
          value: 0,
          label: t("Management.InProgress"),
          color: "#0866FF"
        },
        {
          id: 3,
          value: 0,
          label: t("Management.Cancels"),
          color: "#7A7A7A"
        }
      ];
    }

    // Sử dụng số lượng tasks - DonutChart sẽ tự động tính tỷ lệ phần trăm
    return [
      {
        id: 0,
        value: boardStatistics.totalCompletedTasks,
        label: t("Management.Done"),
        color: "#20DF75"
      },
      {
        id: 1,
        value: boardStatistics.totalFailedTasks,
        label: t("Management.Fail"),
        color: "#FF4655"
      },
      {
        id: 2,
        value: boardStatistics.totalInProgressTasks,
        label: t("Management.InProgress"),
        color: "#0866FF"
      },
      {
        id: 3,
        value: boardStatistics.totalCancelledTasks,
        label: t("Management.Cancel"),
        color: "#7A7A7A"
      }
    ];
  };

  return (
    <div
      className="summary rounded-tl-[8px]"
      style={{ border: "1px solid #EDEDED" }}
    >
      <div
        className="summary-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <h1 className="summary-title">{t("Summary.Title")}</h1>
      </div>

      <div className="summary-content flex justify-between align-center gap-3 mt-3">
        <div className="summary-content-left w-4/6 flex-col flex align-center gap-3">
          <div className="flex gap-3">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  padding: "20px"
                }}
              >
                <span>{t("Summary.Loading")}</span>
              </div>
            ) : (
              workflows.map((workflow, index) => (
                <CardWorkflowInfo
                  key={index}
                  title={workflow.title}
                  yesterday_total={workflow.total_yesterday}
                  now_total={workflow.total_today}
                  changePercent={workflow.changePercent}
                  change={workflow.change}
                />
              ))
            )}
          </div>

          <div style={{ display: "flex", gap: "12px", width: "100%" }}>
            <Box
              sx={{
                width: "66.667%",
                backgroundColor: "#FAFAFA",
                padding: "16px",
                paddingBottom: "0",
                maxHeight: "300px",
                borderRadius: "10px",
                "@media (min-width:1280px) and (max-width:1500px)": {
                  width: "100%"
                }
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#7B7B7B",
                  marginBottom: "10px",
                  display: "block"
                }}
              >
                {t("Summary.Activity")}
              </span>
              <div className="hidden-scroll" style={{ maxHeight: "238px" }}>
                {usersWorkflowInfo.map(user => {
                  return (
                    <UserWorkflowInfo
                      key={user.userId}
                      name={user.userFullName}
                      avatar={user.avatar}
                      isShowTotal={true}
                      total_task={user.totalTasks}
                    />
                  );
                })}
              </div>
            </Box>

            <div
              className="w-1/2"
              style={{
                backgroundColor: "#FAFAFA",
                padding: "16px 16px 14px 16px",
                borderRadius: "10px"
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#7B7B7B",
                  marginBottom: "10px",
                  display: "block"
                }}
              >
                {t("Summary.Notifications")}
              </span>

              <div
                ref={notificationScrollRef}
                className="hidden-scroll"
                style={{ maxHeight: "238px" }}
                onScroll={handleNotificationScroll}
              >
                {notifications.flatMap(group =>
                  group.notifications.map(notification => {
                    // Parse notification.data để lấy Title với HTML
                    let displayTitle = notification.title;
                    try {
                      const parsedData = JSON.parse(notification.data);
                      if (parsedData.Title) {
                        displayTitle = parsedData.Title;
                      }
                    } catch (e) {
                      // Nếu parse lỗi, dùng title mặc định
                      displayTitle = notification.title;
                    }

                    return (
                      <Notification
                        key={notification.id}
                        title={displayTitle}
                        date={notification.created.toString()}
                      />
                    );
                  })
                )}
                {getNotifications.loading && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      color: "#7B7B7B",
                      fontSize: "12px"
                    }}
                  >
                    {t("Summary.NotificationsLoading")}
                  </div>
                )}
                {!hasMoreNotifications && notifications.length > 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      color: "#7B7B7B",
                      fontSize: "12px"
                    }}
                  >
                    {t("Summary.NotificationsNoMore")}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="w-2/2"
            style={{
              backgroundColor: "#FAFAFA",
              borderRadius: "10px",
              padding: "20px 16px 0 16px"
            }}
          >
            <div className="flex align-center justify-between justify-content-between">
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#a6a6a6",
                  display: "block"
                }}
              >
                {t("Summary.Traffic")}
              </span>

              <ButtonGroup
                sx={{
                  border: "1px solid #EDEDED",
                  padding: "1px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  backgroundColor: "#F7F7F7"
                }}
                size="small"
                aria-label="Small button group"
              >
                <button
                  onClick={() => handleChartViewModeChange("completion")}
                  style={{
                    minWidth: "141px",
                    padding: "6px 12px",
                    height: "100%",
                    borderRadius: "4px",
                    color:
                      chartViewMode === "completion" ? "#454545" : "#7B7B7B",
                    background:
                      chartViewMode === "completion"
                        ? "#FFFFFF"
                        : "transparent",
                    fontWeight: chartViewMode === "completion" ? "500" : "400",
                    cursor: "pointer",
                    border: "none"
                  }}
                >
                  {t("Summary.CompletionRate")}
                </button>
                <button
                  onClick={() => handleChartViewModeChange("taskCount")}
                  style={{
                    minWidth: "141px",
                    padding: "6px 12px",
                    height: "100%",
                    borderRadius: "4px",
                    color:
                      chartViewMode === "taskCount" ? "#454545" : "#7B7B7B",
                    background:
                      chartViewMode === "taskCount" ? "#FFFFFF" : "transparent",
                    fontWeight: chartViewMode === "taskCount" ? "500" : "400",
                    cursor: "pointer",
                    border: "none"
                  }}
                >
                  {t("Summary.TaskCount")}
                </button>
              </ButtonGroup>
            </div>

            <div>
              <Box sx={{ width: "100%", height: 316 }}>
                <svg width="0" height="0">
                  <defs>
                    <linearGradient
                      id="areaGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop offset="0%" style={{ stopColor: "#032F9C80" }} />
                      <stop offset="100%" style={{ stopColor: "#075DE803" }} />
                    </linearGradient>
                  </defs>
                </svg>
                <LineChart
                  series={[
                    {
                      data: chartData,
                      area: true,
                      showMark: false,
                      color: "#3b82f6"
                    }
                  ]}
                  xAxis={[{ scaleType: "point", data: chartLabels }]}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                      stroke: "#032F9C"
                    },
                    "& .MuiAreaElement-root": {
                      fill: "url(#areaGradient)"
                    }
                  }}
                  slots={{
                    axisContent: props => (
                      <CustomTooltipContent {...props} mode={chartViewMode} />
                    )
                  }}
                  margin={{ right: 24 }}
                />
              </Box>
            </div>
          </div>
        </div>

        <div
          className="summary-content-right w-2/6"
          style={{ borderRadius: "10px" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "50px",
              backgroundColor: "#FAFAFA",
              padding: "24px",
              borderRadius: "10px",
              "@media (min-width:1280px) and (max-width:1500px)": {
                gap: "24px",
                flexDirection: "column"
              }
            }}
          >
            <div className="flex flex-col gap-10" style={{ width: "100%" }}>
              <div>
                <Button
                  variant="contained"
                  onClick={handleClick}
                  endIcon={
                    <img
                      src={ArrowDropUpIcon}
                      alt="dropdown"
                      style={{ width: "15px", height: "10px" }}
                    />
                  }
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "6px",
                    height: "30px",
                    color: "#021C5E",
                    "&:hover": {
                      backgroundColor: "#FFFFFF"
                    }
                  }}
                >
                  {selectedBoardName || t("Management.SelectWorkflow")}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button"
                  }}
                >
                  {groupedBoards && groupedBoards.projects.length > 0 ? (
                    groupedBoards.projects.map(project => (
                      <NestedMenuItem
                        key={project.typeBoard}
                        label={`${project.typeBoard} (${project.totalCount})`}
                      >
                        {project.boards && project.boards.length > 0 ? (
                          project.boards.map(board => (
                            <MenuItem
                              key={board.boardId}
                              onClick={() => {
                                // Click vào board, gọi API với boardId
                                fetchBoardStatistics(
                                  board.boardId,
                                  board.boardName
                                );
                                handleClose();
                              }}
                            >
                              {board.boardName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>{t("Summary.NoBoards")}</MenuItem>
                        )}
                      </NestedMenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>{t("Summary.Loading")}</MenuItem>
                  )}
                </Menu>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px"
                }}
              >
                <ProgressLine
                  total={boardStatistics?.totalTasks || 0}
                  value={boardStatistics?.totalCompletedTasks || 0}
                  showPercentage={true}
                  label={t("Management.Done")}
                  barColor="#20DF75"
                />
                <ProgressLine
                  total={boardStatistics?.totalTasks || 0}
                  value={boardStatistics?.totalFailedTasks || 0}
                  showPercentage={true}
                  label={t("Management.Fail")}
                  barColor="#FF4655"
                />
                <ProgressLine
                  total={boardStatistics?.totalTasks || 0}
                  value={boardStatistics?.totalInProgressTasks || 0}
                  showPercentage={true}
                  label={t("Management.InProgress")}
                  barColor="#0866FF"
                />
                <ProgressLine
                  total={boardStatistics?.totalTasks || 0}
                  value={boardStatistics?.totalCancelledTasks || 0}
                  showPercentage={true}
                  label={t("Management.Cancel")}
                  barColor="#7A7A7A"
                />
              </div>
            </div>

            <div className="w-[190px]">
              <DonutChart data={getDonutChartData()} showLegend={false} />
            </div>
          </Box>

          <div className="flex flex-col mt-[12px] bg-[#FAFAFA] p-[16px] rounded-[10px]">
            <div className="flex items-center justify-between">
              <TextField
                placeholder={t("Summary.SearchPlaceholder")}
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#7B7B7B" }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: "60%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#E0E0E0"
                    },
                    "&:hover fieldset": {
                      borderColor: "#032F9C"
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#032F9C"
                    }
                  }
                }}
              />

              <Button
                variant="contained"
                onClick={handleSortClick}
                endIcon={
                  <img
                    src={ArrowDropUpIcon}
                    alt="dropdown"
                    style={{ width: "10px", height: "10px" }}
                  />
                }
                sx={{
                  width: "28%",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "6px",
                  height: "30px",
                  fontSize: "14px",
                  padding: "0",
                  color: sortBy ? variableStyles.MainColor : "#7B7B7B",
                  fontWeight: sortBy ? "600" : "400",
                  "&:hover": {
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                {getSortLabel()}
              </Button>
              <Menu
                anchorEl={sortAnchorEl}
                open={sortOpen}
                onClose={handleSortClose}
                MenuListProps={{
                  "aria-labelledby": "sort-button"
                }}
              >
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("inprogress-desc")}
                >
                  {t("Summary.Sort.InProgressDesc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("inprogress-asc")}
                >
                  {t("Summary.Sort.InProgressAsc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("failed-desc")}
                >
                  {t("Summary.Sort.FailedDesc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("failed-asc")}
                >
                  {t("Summary.Sort.FailedAsc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("completed-desc")}
                >
                  {t("Summary.Sort.CompletedDesc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("completed-asc")}
                >
                  {t("Summary.Sort.CompletedAsc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("cancelled-desc")}
                >
                  {t("Summary.Sort.CancelledDesc")}
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: "13px" }}
                  onClick={() => handleSortMenuItemClick("cancelled-asc")}
                >
                  {t("Summary.Sort.CancelledAsc")}
                </MenuItem>
              </Menu>
            </div>

            <div>
              <DataTable data={filteredUsers} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
